import {getSetting, nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router';
import Account, {ImportingStat} from '../../models/accountModel';
import BolProduct from '../../models/bolProductModel';
import {PRODUCTS_ROUTE} from '../../util/constants';
import {downloadFileResponse} from '../../util/downloadUtil';
import {onSetCrumbs} from '../../util/events';
import {AccessKey, ActivateHandle, IdleState} from '../../util/interfaces';
import {
    getProductStatus,
    groupProductsByStatus,
    matchesVisibility,
    ProductStatus,
    ProductVisibility,
    shouldConfirmHide
} from '../../util/productsUtil';
import {generateAllQrCodes, generateQrCode, getProductsV2} from '../../util/requestSender';
import {createErrorMessage} from '../../util/utils';
import {AppContext} from '../misc/appContext';
import {useIdleObserver} from '../misc/idleObserver';
import useRight from '../misc/useRight';
import EditCostsDialogController from './editCostsDialog/editCostsDialogController';
import EditNameDialogController from './editNameDialog/editNameDialogController';
import HideProductDialogController from './hideProductDialog/hideProductDialogController';
import IntroDialogController from './introDialog/introDialogController';
import ProductVATController from '../settings/vat/product/productVATController';
import DefaultVatDialogController from './defaultVatDialog/defaultVatDialogController';
import ImportExportDialogController from './importExportDialog/importExportDialogController';
import Products from './products';

const DEBOUNCE = 500;
const DEFAULT_LIMIT = 20;
const LOAD_MORE_PAD_ROWS = 5;
const NUM_CHECK_DELAY = 5000;

type LoadParams = {
    search?: string | null;
    visibility?: ProductVisibility;
    cursor?: string | null;
    append?: boolean;
    continued?: boolean;
};

export default function ProductsController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const account = context.accountContext.account;
    const user = context.userContext.user;
    const [searchParams, setSearchParams] = useSearchParams();
    const [canSync, canEditVat, canOpenProductSettings, canBulkUpload] = useRight(
        AccessKey.PRODUCT_SETTINGS_SYNC,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PAGE_SETTINGS_PRODUCTS,
        AccessKey.PRODUCT_SETTINGS_UPLOAD
    );
    const [products, setProducts] = useState<BolProduct[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [visibility, setVisibility] = useState<ProductVisibility>('all');
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
    const [startingSync, setStartingSync] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importingCount, setImportingCount] = useState<number | null>(null);
    const statusByEan = useMemo(() => {
        const statuses: Record<string, ProductStatus> = {};
        for (const product of products) statuses[product.ean] = getProductStatus(product);
        return statuses;
    }, [products]);
    const costs = useMemo(() => {
        const prices: Record<string, number | null> = {};
        for (const product of products) prices[product.ean] = product.costPrice;
        return prices;
    }, [products]);
    const groups = useMemo(
        () => groupProductsByStatus(products, statusByEan, visibility),
        [products, statusByEan, visibility]
    );
    const canEditProduct = canEditVat && canOpenProductSettings;
    const showSyncButton = useMemo(() => {
        const hideSyncForUsers: Array<string> = getSetting('hideProductSyncForUsers') ?? [];
        return canSync && !(!!user && hideSyncForUsers.includes(user.id));
    }, [canSync, user?.id]);
    /** The zip covers the whole account, so a filter that empties the list must
     *  not gate it — only an account without any products does. */
    const canDownloadAll = useMemo(
        () => loadingProducts || !!products.length || !!appliedSearch.trim().length || visibility !== 'all',
        [loadingProducts, products.length, appliedSearch, visibility]
    );
    const hasBolConnection = !!account?.getActiveAPIKey();
    const searchDebounce = useRef<number | null>(null);
    const productsAbortController = useRef<AbortController | null>(null);
    const productsRun = useRef(0);
    const loadingMoreRef = useRef(false);
    const appliedSearchRef = useRef<string | null>(null);
    const visibilityRef = useRef<ProductVisibility>('all');
    const cursorRef = useRef<string | null>(null);
    const countTimeout = useRef<number | undefined>(undefined);
    const countRun = useRef(0);
    const wasImporting = useRef(false);
    const pendingEanRef = useRef<string | null>(null);
    const nameDialogRef = useRef<ActivateHandle<BolProduct>>(null);
    const vatDialogRef = useRef<ActivateHandle<BolProduct>>(null);
    const defaultVatDialogRef = useRef<ActivateHandle>(null);
    const importExportDialogRef = useRef<ActivateHandle>(null);
    const costsDialogRef = useRef<ActivateHandle<BolProduct>>(null);
    const hideDialogRef = useRef<ActivateHandle<BolProduct>>(null);
    const introDialogRef = useRef<ActivateHandle>(null);
    useIdleObserver(onIdleStateChange);

    const clearDebounce = useCallback(() => {
        if (searchDebounce.current !== null) {
            window.clearTimeout(searchDebounce.current);
            searchDebounce.current = null;
        }
    }, []);

    const finishLoading = useCallback(() => {
        loadingMoreRef.current = false;
        setLoadingProducts(false);
        setLoadingMore(false);
    }, []);

    const showLoadError = useCallback(
        (message?: string | null) => {
            showDialog(
                createErrorMessage(message ?? t('products.load.failed', 'Het laden van de producten is niet gelukt.')),
                t('products.load.failedTitle', 'Laden mislukt')
            );
        },
        [t]
    );

    const replaceProduct = useCallback((product: BolProduct) => {
        setProducts(current => {
            const next: BolProduct[] = [];
            for (const item of current) {
                if (item.id !== product.id) next.push(item);
                else if (matchesVisibility(getProductStatus(product), visibilityRef.current)) next.push(product);
            }
            return next;
        });
    }, []);

    const loadProducts = useCallback(
        (load: boolean, params: LoadParams) => {
            if (!params.append && !params.continued) clearDebounce();
            if (!account) return;
            const _search = params.append ? appliedSearchRef.current : nullify(params.search ?? '');
            const _visibility = params.visibility ?? visibilityRef.current;
            const _cursor = params.cursor ?? null;
            if (load) setLoadingProducts(true);
            const run = ++productsRun.current;
            productsAbortController.current = getProductsV2({
                data: {
                    account_ids: [account.id],
                    search: _search,
                    limit: DEFAULT_LIMIT,
                    last_product_ean: _cursor,
                    require_meta: false,
                    load_meta: true,
                    visibility: _visibility,
                    expand: ['qr_scan_counts', 'product_costs']
                },
                callback: (data: ResponseData) => {
                    if (run !== productsRun.current) return;
                    productsAbortController.current = null;
                    if (!data.success) {
                        finishLoading();
                        if (!data.handled) showLoadError(data.message);
                        return;
                    }
                    const fetched: BolProduct[] = (data.products ?? []).map(
                        (product: object) => new BolProduct(product)
                    );
                    const nextCursor: string | null = data.last_product_ean ?? null;
                    const more = !!data.has_more && nextCursor !== null;
                    cursorRef.current = nextCursor;
                    setHasMore(more);
                    if (params.append)
                        setProducts(current => {
                            const seen = new Set(current.map((product: BolProduct) => product.id));
                            return current.concat(fetched.filter((product: BolProduct) => !seen.has(product.id)));
                        });
                    else {
                        appliedSearchRef.current = _search;
                        setAppliedSearch(_search ?? '');
                        setProducts(fetched);
                    }
                    if (more && !fetched.length) {
                        loadProducts(load, {
                            ...params,
                            search: _search ?? '',
                            cursor: nextCursor,
                            continued: true
                        });
                        return;
                    }
                    finishLoading();
                },
                errorCallback: () => {
                    if (run !== productsRun.current) return;
                    productsAbortController.current = null;
                    finishLoading();
                    showLoadError();
                },
                onAbort: () => {
                    if (run !== productsRun.current) return;
                    productsAbortController.current = null;
                    finishLoading();
                }
            });
        },
        [account, clearDebounce, finishLoading, showLoadError]
    );

    const attemptLoadMore = useCallback(
        (ix: number) => {
            if (!hasMore || loadingProducts || loadingMoreRef.current) return;
            if (ix < products.length - LOAD_MORE_PAD_ROWS) return;
            loadingMoreRef.current = true;
            setLoadingMore(true);
            loadProducts(false, {cursor: cursorRef.current, append: true});
        },
        [hasMore, loadingProducts, products.length, loadProducts]
    );

    const onEditName = useCallback((product: BolProduct) => {
        nameDialogRef.current?.activate(product);
    }, []);

    const onEditVat = useCallback((product: BolProduct) => {
        vatDialogRef.current?.activate(product.clone());
    }, []);

    const onEditCosts = useCallback((product: BolProduct) => {
        costsDialogRef.current?.activate(product);
    }, []);

    const toggleHidden = useCallback(
        (product: BolProduct) => {
            if (updatingIds.has(product.id)) return;
            const hide = !product.hiddenByUser;
            const updated = product.clone();
            updated.hiddenByUser = hide;
            setUpdatingIds(current => new Set(current).add(product.id));
            updated.update(
                false,
                (saved: BolProduct, success: boolean, data: ResponseData) => {
                    removeUpdatingId(product.id);
                    if (success) {
                        showSnackbar(
                            hide
                                ? t('products.hide.done', 'Het product is verborgen')
                                : t('products.show.done', 'Het product wordt weer getoond')
                        );
                        replaceProduct(saved);
                    } else if (!data.handled) showToggleHiddenError(hide, data.message);
                },
                () => {
                    removeUpdatingId(product.id);
                    showToggleHiddenError(hide);
                }
            );

            function removeUpdatingId(id: string) {
                setUpdatingIds(current => {
                    const next = new Set(current);
                    next.delete(id);
                    return next;
                });
            }

            function showToggleHiddenError(hide: boolean, message?: string | null) {
                showDialog(
                    createErrorMessage(
                        message ??
                            (hide
                                ? t('products.hide.failed', 'Het verbergen van het product is niet gelukt.')
                                : t('products.show.failed', 'Het opnieuw tonen van het product is niet gelukt.'))
                    ),
                    hide
                        ? t('products.hide.failedTitle', 'Verbergen mislukt')
                        : t('products.show.failedTitle', 'Tonen mislukt')
                );
            }
        },
        [updatingIds, replaceProduct, t]
    );

    const onToggleHidden = useCallback(
        (product: BolProduct) => {
            if (!product.hiddenByUser && shouldConfirmHide(user)) hideDialogRef.current?.activate(product);
            else toggleHidden(product);
        },
        [user, toggleHidden]
    );

    const onClickDownloadQrCode = useCallback(
        (product: BolProduct) => {
            if (downloadingIds.has(product.id)) return;
            setDownloadingIds(current => new Set(current).add(product.id));
            generateQrCode({
                account_id: product.accountId,
                product_id: product.id,
                callback: (data: ResponseData, response: Response) => {
                    removeDownloadingId(product.id);
                    if (data.status === 'success') downloadFileResponse(response, `review-qr-${product.ean}.png`);
                    else if (!data.handled) showDownloadError(data.message);
                },
                errorCallback: () => {
                    removeDownloadingId(product.id);
                    showDownloadError();
                }
            });

            function removeDownloadingId(id: string) {
                setDownloadingIds(current => {
                    const next = new Set(current);
                    next.delete(id);
                    return next;
                });
            }

            function showDownloadError(message?: string | null) {
                showDialog(
                    createErrorMessage(
                        message ?? t('products.download.failed', 'Het downloaden van de QR-code is niet gelukt.')
                    ),
                    t('products.download.failedTitle', 'Downloaden mislukt')
                );
            }
        },
        [downloadingIds, t]
    );

    function onIdleStateChange(currentState: IdleState) {
        if (currentState === IdleState.IDLE) clearCountCheck();
        else checkImportingCount();
    }

    function onChangeSearch(value: string) {
        clearDebounce();
        setSearch(value);
        searchDebounce.current = window.setTimeout(() => {
            cancelRequests();
            loadProducts(true, {search: value});
        }, DEBOUNCE);
    }

    function onSearch() {
        clearDebounce();
        cancelRequests();
        loadProducts(true, {search});
    }

    function onChangeVisibility(value: ProductVisibility) {
        if (value === visibility) return;
        setVisibility(value);
        visibilityRef.current = value;
        cancelRequests();
        loadProducts(true, {search, visibility: value});
    }

    function onNameSaved(product: BolProduct) {
        replaceProduct(product);
    }

    function onVatSaved(product: BolProduct) {
        replaceProduct(product);
    }

    function onClickDownloadAllQrCodes() {
        if (downloadingAll || !account) return;
        setDownloadingAll(true);
        generateAllQrCodes({
            account_id: account.id,
            callback: (data: ResponseData, response: Response) => {
                setDownloadingAll(false);
                if (data.status === 'success') downloadFileResponse(response, 'review-qr.zip');
                else if (!data.handled) showDownloadAllError(data.message);
            },
            errorCallback: () => {
                setDownloadingAll(false);
                showDownloadAllError();
            }
        });
    }

    function onClickSync() {
        if (!account || startingSync || importing) return;
        setStartingSync(true);
        BolProduct.importAsync(
            account.id,
            false,
            (_: Account | null, success: boolean, data: ResponseData) => {
                setStartingSync(false);
                if (success) {
                    checkImportingCount();
                    showSnackbar(t('products.sync.started', 'De producten worden opgehaald'));
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ?? t('products.sync.failed', 'Het ophalen van de producten is niet gelukt.')
                        ),
                        t('products.sync.failedTitle', 'Ophalen mislukt')
                    );
            },
            onSyncFailed
        );
    }

    function onSyncFailed() {
        setStartingSync(false);
        showDialog(
            createErrorMessage(t('products.sync.failed', 'Het ophalen van de producten is niet gelukt.')),
            t('products.sync.failedTitle', 'Ophalen mislukt')
        );
    }

    function onClickDefaultVat() {
        defaultVatDialogRef.current?.activate();
    }

    function onClickImportExport() {
        importExportDialogRef.current?.activate();
    }

    function onClickIntro() {
        introDialogRef.current?.activate();
    }

    function onProductsChanged() {
        cancelRequests();
        loadProducts(true, {search});
    }

    function showDownloadAllError(message?: string | null) {
        showDialog(
            createErrorMessage(
                message ?? t('products.download.failed', 'Het downloaden van de QR-code is niet gelukt.')
            ),
            t('products.download.failedTitle', 'Downloaden mislukt')
        );
    }

    function checkImportingCount() {
        if (!account) return;
        const run = ++countRun.current;
        account
            .getImportingProductsCount()
            .then((data: ImportingStat) => {
                if (run !== countRun.current) return;
                window.clearTimeout(countTimeout.current);
                if (data.importing) countTimeout.current = window.setTimeout(checkImportingCount, NUM_CHECK_DELAY);
                setImportingCount(data.products);
                setImporting(data.importing);
            })
            .catch(() => {
                if (run === countRun.current) setImporting(false);
            });
    }

    function cancelRequests() {
        if (productsAbortController.current) {
            productsRun.current++;
            productsAbortController.current.abort();
            productsAbortController.current = null;
            finishLoading();
        }
    }

    function clearCountCheck() {
        countRun.current++;
        window.clearTimeout(countTimeout.current);
        countTimeout.current = undefined;
    }

    useEffect(() => {
        onSetCrumbs([{label: t('products.crumb', 'Producten'), route: PRODUCTS_ROUTE}]);
        const ean = nullify(searchParams.get('ean'));
        if (ean) {
            pendingEanRef.current = ean;
            setSearchParams({}, {replace: true});
        }
        return function () {
            clearCountCheck();
        };
    }, []);

    useEffect(() => {
        cancelRequests();
        const initialSearch = pendingEanRef.current ?? '';
        pendingEanRef.current = null;
        setSearch(initialSearch);
        if (!account) {
            setProducts([]);
            setAppliedSearch('');
            appliedSearchRef.current = null;
            setHasMore(false);
            setLoadingProducts(false);
        } else {
            loadProducts(true, {search: initialSearch});
            checkImportingCount();
        }
        return function () {
            clearDebounce();
            cancelRequests();
            clearCountCheck();
        };
    }, [account?.id]);

    useEffect(() => {
        if (wasImporting.current && !importing) onProductsChanged();
        wasImporting.current = importing;
    }, [importing]);

    return (
        <>
            <Products
                onChangeSearch={onChangeSearch}
                onSearch={onSearch}
                onChangeVisibility={onChangeVisibility}
                onEditName={onEditName}
                onEditVat={onEditVat}
                onEditCosts={onEditCosts}
                onToggleHidden={onToggleHidden}
                onClickDownload={onClickDownloadQrCode}
                onClickDownloadAllQrCodes={onClickDownloadAllQrCodes}
                onClickSync={onClickSync}
                onRowVisible={attemptLoadMore}
                costs={costs}
                onClickDefaultVat={onClickDefaultVat}
                onClickIntro={onClickIntro}
                onClickImportExport={onClickImportExport}
                canImportExport={canBulkUpload}
                groups={groups}
                statusByEan={statusByEan}
                search={search}
                appliedSearch={appliedSearch}
                visibility={visibility}
                loadingProducts={loadingProducts}
                loadingMore={loadingMore}
                downloadingIds={downloadingIds}
                downloadingAll={downloadingAll}
                canDownloadAll={canDownloadAll}
                updatingIds={updatingIds}
                canEditProduct={canEditProduct}
                showSyncButton={showSyncButton}
                syncing={startingSync || importing}
                importing={importing}
                importingCount={importingCount}
                hasBolConnection={hasBolConnection}
            />
            <EditNameDialogController ref={nameDialogRef} onSuccess={onNameSaved} />
            <ProductVATController ref={vatDialogRef} onSuccess={onVatSaved} onEditDefaults={onClickDefaultVat} />
            <DefaultVatDialogController ref={defaultVatDialogRef} />
            <ImportExportDialogController ref={importExportDialogRef} onProductsChanged={onProductsChanged} />
            <EditCostsDialogController ref={costsDialogRef} onSuccess={onProductsChanged} />
            <HideProductDialogController ref={hideDialogRef} onHidden={replaceProduct} />
            <IntroDialogController ref={introDialogRef} />
        </>
    );
}
